import React, { useEffect, useState } from "react";
import { Avatar, Tag, Upload, Spin, App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import ImgCrop from "antd-img-crop";
import {
  HiCamera,
  HiMail,
  HiPhone,
  HiUser,
  HiOfficeBuilding,
  HiShieldCheck,
} from "react-icons/hi";
import { profile } from "../store/slice/auth";
import api from "../store/client";

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0">
      <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center shrink-0">
        <Icon className="text-orange-500 text-base" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 leading-none mb-0.5">{label}</p>
        <p className="text-sm font-medium text-gray-800 truncate">{value}</p>
      </div>
    </div>
  );
}

function ProfilePage() {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const { user, isLoading, fullname, username } = useSelector((s) => s.auth);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    dispatch(profile());
  }, [dispatch]);

  const avatarSrc = user?.profilePicture
    ? `${(api.defaults.baseURL ?? "").replace(/\/$/, "")}/identity/user/ProfileImages/${user.profilePicture}`
    : null;

  const handleUpload = async (file) => {
    setUploading(true);
    const data = new FormData();
    data.append("file", file);
    try {
      await api.post("/identity/user/UpdateProfilePic", data, {
        headers: { "content-type": "multipart/form-data" },
      });
      dispatch(profile());
      message.success("Profile picture updated");
    } catch {
      message.error("Failed to update profile picture");
    } finally {
      setUploading(false);
    }
    return false;
  };

  if (isLoading && !user) {
    return (
      <div className="flex justify-center items-center h-48">
        <Spin size="large" />
      </div>
    );
  }

  const u = user ?? {};
  const roles = u.roles ?? [];
  const initials = (u.fullName ?? fullname ?? "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="max-w-2xl mx-auto">
      {/* Profile card */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Orange banner */}
        <div className="h-28 bg-gradient-to-r from-orange-500 to-orange-400" />

        {/* Avatar — overlaps the banner */}
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 -mt-12 mb-6">
            <ImgCrop rotationSlider>
              <Upload
                showUploadList={false}
                beforeUpload={handleUpload}
                accept="image/*"
              >
                <div className="relative cursor-pointer group">
                  <Avatar
                    size={96}
                    src={avatarSrc}
                    style={{
                      backgroundColor: "#f97316",
                      fontSize: 32,
                      border: "4px solid white",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    }}
                  >
                    {!avatarSrc && initials}
                  </Avatar>
                  <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                    {uploading ? (
                      <Spin size="small" />
                    ) : (
                      <HiCamera className="text-white text-xl" />
                    )}
                  </div>
                </div>
              </Upload>
            </ImgCrop>

            {/* Role tags top-right */}
            {roles.length > 0 && (
              <div className="flex flex-wrap gap-1 sm:mb-1">
                {roles.map((role) => (
                  <Tag key={role} color="orange" className="rounded-full px-3">
                    {role}
                  </Tag>
                ))}
              </div>
            )}
          </div>

          {/* Name & email */}
          <h2 className="text-2xl font-bold text-gray-900 leading-tight">
            {u.fullName ?? fullname ?? "—"}
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {u.email ?? username ?? "—"}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Hover the photo to change your profile picture
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 mx-6" />

        {/* Info rows */}
        <div className="px-6 py-2">
          <InfoRow icon={HiUser} label="First Name" value={u.firstName} />
          <InfoRow icon={HiUser} label="Last Name" value={u.lastName} />
          <InfoRow icon={HiMail} label="Email" value={u.email ?? username} />
          <InfoRow icon={HiPhone} label="Phone Number" value={u.phoneNumber} />
          {u.tenantNames?.length > 0 && (
            <InfoRow
              icon={HiOfficeBuilding}
              label="Tenant"
              value={u.tenantNames.join(", ")}
            />
          )}
          {roles.length > 0 && (
            <div className="flex items-start gap-3 py-3">
              <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center shrink-0 mt-0.5">
                <HiShieldCheck className="text-orange-500 text-base" />
              </div>
              <div>
                <p className="text-xs text-gray-400 leading-none mb-1.5">
                  Roles
                </p>
                <div className="flex flex-wrap gap-1">
                  {roles.map((role) => (
                    <Tag key={role} color="blue">
                      {role}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
